import { useQuery } from "@tanstack/react-query";
import client from "shared/utils/axios-config";
import { useAuth } from "context/AuthContextProvider";
import { isSystemAdmin } from "shared/utils/role";
import { localizeServerDateLabels } from "shared/utils/serverDateLabels";
import {
  AnalyticsRange,
  ClassAnalytics,
  ClientAnalytics,
  ClientInactiveAnalytics,
  ClientRevenueAnalytics,
  CustomDayRange,
  CustomMonthRange,
  DiscountCodeAnalytics,
  ExpiringSoonThresholdDays,
  InactivityThresholdDays,
  InstructorAnalytics,
  LoyaltyAnalytics,
  MonthlyStatement,
  RetentionBound,
  PackageAnalyticsExpired,
  PackageLowCreditsAnalytics,
  PackageAnalyticsOverview,
  ReferralAnalytics,
  RewardUsageAnalytics,
  SalesPackagesAnalytics,
  SalesServicesAnalytics,
  SalesTrendAnalytics,
  SalesTrendMonths,
} from "shared/types";

/* eslint-disable react-hooks/rules-of-hooks */

// Plain fetch (NOT a hook) for the PDF export path. The page's own query is
// capped at the server's display limit, so a PDF asking for more rows than
// the table holds needs a one-off request carrying `row_limit`. Keeping this
// off the page query is the point: the bigger payload is paid only by whoever
// actually downloads, never on every visit. The server clamps row_limit to
// its own ceiling, so an inflated value can't be used to pull unbounded data.
export const fetchAnalyticsRows = async <T>(
  family: "sales" | "client" | "loyalty" | "referral",
  params: Record<string, unknown>,
  rowLimit: number,
): Promise<T | undefined> => {
  const res = await client.get(`/v1/analytics/${family}`, {
    params: { ...params, row_limit: rowLimit },
  });
  // Outside react-query, so the query defaults' select doesn't reach it.
  if (res.status === 200 && !res.data?.error) return localizeServerDateLabels(res.data.data);
};

const getClassAnalytics = (range: AnalyticsRange, customRange: CustomDayRange = null) => {
  return useQuery({
    queryKey: ["analytics", "class", range, customRange],
    queryFn: async (): Promise<ClassAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/class", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to }
          : { range },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// includeHealthMetrics is an opt-in extra (Performa Paket/Membership only —
// see PackageOverviewContent.tsx) that triggers extra server-side queries
// (repurchase rate, credit utilization, etc.); Pendapatan and every other
// caller of this same hook omits it, so they never pay that query cost.
const getPackageAnalyticsOverview = (
  range: AnalyticsRange,
  type: "paket" | "membership",
  // "all" sends no promo filter (Performa lists regular and promo products).
  promo: "reguler" | "promo" | "all" = "reguler",
  customRange: CustomDayRange = null,
  includeHealthMetrics = false,
  includeCreditsUsed = false,
  // Which field the server should pre-sort `table` by when there's no
  // active on-screen sort — must match the caller's own on-screen
  // defaultSortOrder, or a sort-state-less PDF/Excel export silently drifts
  // from what's shown on screen. Performa passes "active" (most active
  // first); Pendapatan's reguler view passes "sales" (its own default is
  // revenue, not activity/count). Promo view ignores this entirely (see the
  // backend).
  defaultSort?: "sold" | "sales" | "active",
) => {
  return useQuery({
    queryKey: [
      "analytics",
      "package",
      "overview",
      range,
      type,
      promo,
      customRange,
      includeHealthMetrics,
      includeCreditsUsed,
      defaultSort,
    ],
    queryFn: async (): Promise<PackageAnalyticsOverview | undefined> => {
      const healthParam = includeHealthMetrics ? { include_health_metrics: "true" } : {};
      const creditsUsedParam = includeCreditsUsed ? { include_credits_used: "true" } : {};
      const defaultSortParam = defaultSort ? { default_sort: defaultSort } : {};
      const promoParam = promo === "all" ? {} : { promo };
      const res = await client.get("/v1/analytics/package", {
        params: customRange
          ? {
              from_date: customRange.from,
              to_date: customRange.to,
              type,
              ...promoParam,
              view: "overview",
              ...healthParam,
              ...creditsUsedParam,
              ...defaultSortParam,
            }
          : {
              range,
              type,
              ...promoParam,
              view: "overview",
              ...healthParam,
              ...creditsUsedParam,
              ...defaultSortParam,
            },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// The Pendapatan dari Paket/Membership (Promo) report — always all-time,
// always promo-scoped, no range/customRange param at all (a promo's own
// bounded lifetime is the window; see PendapatanProdukPromoContent.tsx).
// KPI values (Total Promo / Promo Aktif) are derived client-side from
// `table`; total_sales/best_seller_by_revenue ride the response as-is.
const getPackagePromoOverview = (type: "paket" | "membership") => {
  return useQuery({
    queryKey: ["analytics", "package", "overview", "promo", "all_time", type],
    queryFn: async (): Promise<PackageAnalyticsOverview | undefined> => {
      const res = await client.get("/v1/analytics/package", {
        // include_credits_used unconditionally — same pattern as
        // PendapatanProdukContent.tsx's always-true call: the backend's own
        // !unlimited gate already no-ops it for Membership, so a single
        // request shape works for both types (see PendapatanProdukPromoContent.tsx's
        // Kredit Terpakai/Pendapatan dari Booking columns).
        params: {
          type,
          promo: "promo",
          all_time: "true",
          view: "overview",
          include_credits_used: "true",
        },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// The compare report — mixed reguler+promo (no `promo` param, same as
// omitting it on any other caller of this endpoint). Defaults to all-time
// (same reasoning as getPackagePromoOverview: a bounded-lifetime promo on
// one side of the comparison makes a rolling range meaningless), but the
// admin can opt into a range for the "which regular tier is trending"
// use case — see PackageComparisonContent.tsx's range toggle. Note only
// the `sold` field in the response actually moves between the two modes;
// active/credits_remaining/expiring_soon/last_purchase are range-independent
// in the backend handler regardless. Custom range is day-precision
// (CustomDayRange, `from_date`/`to_date`) rather than the month-granularity
// CustomMonthRange every other report's custom range uses — a promo can
// start/end mid-month, so bracketing it by month can't isolate its exact
// window (see getCustomDayRange on the backend).
const getPackageComparisonOverview = (
  type: "paket" | "membership",
  allTime: boolean = true,
  range: AnalyticsRange = "1m",
  customDayRange: CustomDayRange = null,
) => {
  return useQuery({
    queryKey: [
      "analytics",
      "package",
      "overview",
      "compare",
      allTime ? "all_time" : range,
      type,
      customDayRange,
    ],
    queryFn: async (): Promise<PackageAnalyticsOverview | undefined> => {
      // include_buyer_mix — only Bandingkan's New vs. Existing Buyer Split
      // chart needs this; every other caller of this endpoint omits it.
      const res = await client.get("/v1/analytics/package", {
        params: allTime
          ? { type, all_time: "true", view: "overview", include_buyer_mix: "true" }
          : customDayRange
            ? {
                type,
                from_date: customDayRange.from,
                to_date: customDayRange.to,
                view: "overview",
                include_buyer_mix: "true",
              }
            : { type, range, view: "overview", include_buyer_mix: "true" },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// "Expiring soon" uses a threshold (not a lookback window) — see
// getClientInactiveAnalytics for the same shape. "Recently expired" IS a
// rolling window, same as every other range-based report; range/customRange
// only affects the "expired" side of the response (see analytics.ts).
// `enabled` lets Status Paket wait for the studio settings that pick the
// starting window, so the report does not load twice.
const getPackageAnalyticsExpired = (
  type: "paket" | "membership",
  expiringSoonDays: ExpiringSoonThresholdDays,
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
  enabled = true,
) => {
  return useQuery({
    enabled,
    queryKey: ["analytics", "package", "expired", type, expiringSoonDays, range, customRange],
    queryFn: async (): Promise<PackageAnalyticsExpired | undefined> => {
      const res = await client.get("/v1/analytics/package", {
        params: customRange
          ? {
              type,
              view: "expired",
              expiring_soon_days: expiringSoonDays,
              from_date: customRange.from,
              to_date: customRange.to,
            }
          : { type, view: "expired", expiring_soon_days: expiringSoonDays, range },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// Kredit Akan Habis: a credits threshold, not a window (paket-only — a
// membership has no credit pool to run down). `enabled` exists so the
// combined membership status page can call this unconditionally (rules of
// hooks) without firing a paket request it will never show.
const getPackageAnalyticsLowCredits = (threshold: number, enabled = true) => {
  return useQuery({
    enabled,
    queryKey: ["analytics", "package", "low_credits", threshold],
    queryFn: async (): Promise<PackageLowCreditsAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/package", {
        params: { type: "paket", view: "low_credits", low_credit_threshold: threshold },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

const getSalesPackagesAnalytics = (
  range: AnalyticsRange,
  type: "paket" | "membership",
  customRange: CustomDayRange = null,
  // Pembelian's Diskon filter (code / loyalty / referral / none). The server
  // applies it before its 250-row cap, so the list holds every matching sale
  // in the period; the KPIs and total_rows ignore it.
  discountFilter: string[] = [],
  // Pembelian's voided switch. Voided sales are never in the KPIs; this only
  // decides whether they are LISTED. An older server strips the param (zod)
  // and simply never lists them.
  showVoided: boolean = false,
) => {
  const period = customRange
    ? { from_date: customRange.from, to_date: customRange.to }
    : { range };
  return useQuery({
    queryKey: [
      "analytics",
      "sales",
      "packages",
      range,
      type,
      customRange,
      discountFilter,
      showVoided,
    ],
    queryFn: async (): Promise<SalesPackagesAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/sales", {
        params: {
          ...period,
          type,
          view: "packages",
          ...(discountFilter.length ? { discount_source: discountFilter.join(",") } : {}),
          ...(showVoided ? { show_voided: "true" } : {}),
        },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
    // Changing only the filter or the voided switch keeps the current data on
    // screen while the new list loads, so the KPI cards (the same either way)
    // don't flash back to skeletons. A new period or type loads from scratch.
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey;
      return key &&
        key[3] === range &&
        key[4] === type &&
        JSON.stringify(key[5]) === JSON.stringify(customRange)
        ? previous
        : undefined;
    },
  });
};

const getSalesServicesAnalytics = (
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
) => {
  return useQuery({
    queryKey: ["analytics", "sales", "services", range, customRange],
    queryFn: async (): Promise<SalesServicesAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/sales", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to, view: "services" }
          : { range, view: "services" },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// No range param — the preset is months, not a day-count window. The
// custom from/to month range works the same as on the other reports.
const getSalesTrendAnalytics = (
  months: SalesTrendMonths,
  customRange: CustomMonthRange = null,
) => {
  return useQuery({
    queryKey: ["analytics", "sales", "trend", months, customRange],
    queryFn: async (): Promise<SalesTrendAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/sales", {
        params: customRange
          ? { from: customRange.from, to: customRange.to, view: "trend" }
          : { months, view: "trend" },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

const getInstructorAnalytics = (
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
) => {
  return useQuery({
    queryKey: ["analytics", "instructor", range, customRange],
    queryFn: async (): Promise<InstructorAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/instructor", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to }
          : { range },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

const getClientAnalytics = (range: AnalyticsRange, customRange: CustomDayRange = null) => {
  return useQuery({
    queryKey: ["analytics", "client", range, customRange],
    queryFn: async (): Promise<ClientAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/client", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to }
          : { range },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

const getClientRevenueAnalytics = (
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
) => {
  return useQuery({
    queryKey: ["analytics", "client", "revenue", range, customRange],
    queryFn: async (): Promise<ClientRevenueAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/client", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to, view: "revenue" }
          : { range, view: "revenue" },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// No range param — same reasoning as getPackageAnalyticsExpired: "days since
// last activity" is a threshold, not a lookback window to pick.
const getClientInactiveAnalytics = (days: InactivityThresholdDays) => {
  return useQuery({
    queryKey: ["analytics", "client", "inactive", days],
    queryFn: async (): Promise<ClientInactiveAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/client", {
        params: { days, view: "inactive" },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

const getLoyaltyAnalytics = (
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
) => {
  return useQuery({
    queryKey: ["analytics", "loyalty", range, customRange],
    queryFn: async (): Promise<LoyaltyAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/loyalty", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to }
          : { range },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// The retention bounds belong to the retention meter alone, which is a
// snapshot over its own window — but they still key the query, because
// changing either end changes the payload.
const getReferralAnalytics = (
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
  retentionFrom?: RetentionBound,
  retentionTo?: RetentionBound,
) => {
  return useQuery({
    queryKey: [
      "analytics",
      "referral",
      range,
      customRange,
      retentionFrom,
      retentionTo,
    ],
    queryFn: async (): Promise<ReferralAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/referral", {
        params: {
          ...(customRange
            ? { from_date: customRange.from, to_date: customRange.to }
            : { range }),
          ...(retentionFrom
            ? {
                retention_from_value: retentionFrom.value,
                retention_from_unit: retentionFrom.unit,
              }
            : {}),
          ...(retentionTo
            ? {
                retention_to_value: retentionTo.value,
                retention_to_unit: retentionTo.unit,
              }
            : {}),
        },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// Pemakaian Reward: same window params as the loyalty report.
const getRewardUsageAnalytics = (
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
) => {
  return useQuery({
    queryKey: ["analytics", "reward-usage", range, customRange],
    queryFn: async (): Promise<RewardUsageAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/reward-usage", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to }
          : { range },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// Performa Kode Diskon: same window params as the loyalty report.
const getDiscountCodeAnalytics = (
  range: AnalyticsRange,
  customRange: CustomDayRange = null,
) => {
  return useQuery({
    queryKey: ["analytics", "discount-code", range, customRange],
    queryFn: async (): Promise<DiscountCodeAnalytics | undefined> => {
      const res = await client.get("/v1/analytics/discount-code", {
        params: customRange
          ? { from_date: customRange.from, to_date: customRange.to }
          : { range },
      });
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

// Monthly Statement of Account — the studio's own sales + tax rollup, no
// range param (the server returns every month with at least one sale).
// Never fired by a SYSTEM ADMIN: their token has no business_id, the v2
// /business mount is admin-only, and the 401 it answers would sign them
// out of the whole dashboard (the useBusinessSettings rule; System Review S6).
const getMonthlyStatements = () => {
  const { state } = useAuth();
  const sysAdmin = isSystemAdmin(state.user?.roles);
  return useQuery({
    enabled: !sysAdmin,
    queryKey: ["business", "monthly-statements"],
    queryFn: async (): Promise<MonthlyStatement[] | undefined> => {
      const res = await client.get("/v2/business/monthly-statements");
      if (res.status === 200 && !res.data?.error) {
        return res.data.data;
      }
    },
  });
};

export {
  getLoyaltyAnalytics,
  getReferralAnalytics,
  getRewardUsageAnalytics,
  getDiscountCodeAnalytics,
  getClassAnalytics,
  getPackageAnalyticsOverview,
  getPackagePromoOverview,
  getPackageComparisonOverview,
  getPackageAnalyticsExpired,
  getPackageAnalyticsLowCredits,
  getSalesPackagesAnalytics,
  getSalesServicesAnalytics,
  getSalesTrendAnalytics,
  getInstructorAnalytics,
  getClientAnalytics,
  getClientRevenueAnalytics,
  getClientInactiveAnalytics,
  getMonthlyStatements,
};
